import React, {useContext, useEffect, useMemo} from 'react';
import RequestInstitutionModal from 'src/components/RequestInstitutionModal';
import BaseStep from 'shared/registration/components/steps/base-step';
import {EducationPhase} from 'src/types/enums';
import {useFormikContext} from 'formik';
import {useBoolean} from 'src/hooks/useBoolean';
import HighSchoolInstitutionTypeaheadView from 'src/views/high-school/high-school-institution-typeahead-view';
import {OnboardingContext} from 'src/components/Onboarding/context';
import {formatCoordinates} from 'src/utils/helpers';
import {ObjectOf} from 'src/types/store';
import {CurrentInstitutionForm} from 'src/types/onboarding';
import {ImpostorModeContext} from 'src/components/ImpostorModeContext';
import {useLocation} from 'react-router-dom';

const getEducationPhaseText = (phase: EducationPhase): string => {
  switch (phase) {
    case EducationPhase.COMMUNITY_COLLEGE:
      return 'community college';
    case EducationPhase.HIGH_SCHOOL:
      return 'high school';
    default:
      console.warn(`No education phase mapping for [${phase}]`);
      return '';
  }
};

const getText = (
  isImpostor: boolean,
  firstName: string,
  educationPhase: EducationPhase
): ObjectOf<string> => {
  const phase = getEducationPhaseText(educationPhase);
  return {
    header: `Where ${isImpostor ? `does ${firstName}` : 'do you'} go to ${phase}?`,
  };
};

export default function HighSchoolInstitutionStep() {
  const form = useFormikContext<CurrentInstitutionForm>();
  const onboardingContext = useContext(OnboardingContext);
  const ineligibleCountry = form.values.ineligibleCountry;
  const {pathname} = useLocation();

  const eligibilityPage = () => pathname.split('/').pop() === 'eligibility';

  useEffect(() => {
    if (ineligibleCountry) {
      onboardingContext.updatePrimaryButton({
        controlled: true,
        text: 'Delete Account',
        color: 'red',
      });
    } else if (onboardingContext.primaryButton.controlled) {
      onboardingContext.resetPrimaryButton();
    }
  }, [ineligibleCountry]);

  const {firstName, educationPhase} = form.values;

  const impostorModeContext = useContext(ImpostorModeContext);

  const text = useMemo(
    () => getText(impostorModeContext.isImpostor, firstName, educationPhase as EducationPhase),
    [impostorModeContext.isImpostor, firstName, educationPhase]
  );
  const expanded = useBoolean(false);
  const location = formatCoordinates(form.values.latitude, form.values.longitude);
  const body = (
    <>
      <HighSchoolInstitutionTypeaheadView
        firstName={firstName}
        isImpostor={impostorModeContext.isImpostor}
        expansion={expanded}
        location={location}
        parentId={form.getFieldMeta('currentInstitution.parentId')}
        institutionName={form.getFieldMeta('currentInstitution.name')}
        postalCodeText={form.values.postalCode}
      />
      <RequestInstitutionModal
        educationPhase={educationPhase}
        impostorMode={impostorModeContext.isImpostor}
        firstName={firstName}
        userId={form.values._id}
        type="high-school"
      />
    </>
  );

  if (eligibilityPage()) {
    return <span className="institution-step">{body}</span>;
  } else {
    return (
      <BaseStep stepHeader={text.header} className="institution-step" required>
        {body}
      </BaseStep>
    );
  }
}
